import { cn, Field } from '@sjuni/ui';
import { EditorContent, useEditor } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import {
  copyLocale,
  hasContent,
  REQUIRED_LOCALE,
  setLocale,
  type TranslatedValue,
  valueFor,
} from '@/lib/forms/translatable';

import { cleanPastedHtml, looksLikeOfficePaste } from './pasteCleanup';
import { richTextExtensions } from './richTextExtensions';
import { RichTextToolbar } from './RichTextToolbar';

/**
 * docs/05-ADMIN-DASHBOARD.md §4.2 — the rich-text editor, wrapped in §4.1's
 * per-locale field.
 *
 * The two are one component because a body is a translatable field like any
 * other: `body` on `news` is `{ka: '<p>…</p>', en: null}`, and an editor
 * switching to English needs the *editor* to switch with them, not a second
 * editor beside the first.
 *
 * Switching locale replaces the document. That is the only unusual thing in
 * here and it is deliberate: TipTap owns its content, so the value has to be
 * pushed in when the locale changes and pulled out when the editor changes,
 * without the two chasing each other. The guard is comparing against what the
 * editor already holds before setting it.
 */
export interface RichTextFieldProps {
  label: string;
  value: TranslatedValue | null | undefined;
  onChange: (next: TranslatedValue) => void;
  hint?: string;
  error?: string;
  /** Georgian required. §6 makes `body` one of the two fields that is. */
  required?: boolean;
  disabled?: boolean;
}

export function RichTextField({
  label,
  value,
  onChange,
  hint,
  error,
  required = false,
  disabled,
}: RichTextFieldProps) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const { hasRole } = useAuth();

  const [showSource, setShowSource] = useState(false);

  /*
   * `handlePaste` is defined inside the `useEditor` options, before the
   * editor it needs exists. Reading through a ref is the way out of that
   * circle; closing over the binding would capture `undefined` forever.
   */
  // `useRef<T>(null)` types the ref as read-only; the mutable form takes the
  // null in the parameter type instead.
  const editorRef = useRef<ReturnType<typeof useEditor> | null>(null);

  /*
   * True while content is being pushed *into* the editor.
   *
   * Without it the field wipes the record it just loaded. The editor is
   * created before the record arrives, so its first content is empty; when
   * the form is filled and the effect below pushes the real HTML in, TipTap
   * emits an update, `onUpdate` calls `onChange` with the *stale* value it
   * closed over, and the body goes back to being blank. It looked like the
   * API returning nothing — the API was returning it and the editor was
   * throwing it away.
   */
  const applying = useRef(false);

  const html = valueFor(value, locale);

  const editor = useEditor({
    extensions: richTextExtensions,
    content: html,
    editable: !disabled,
    editorProps: {
      attributes: {
        /*
         * `prose` from the typography plugin, so what the editor shows is
         * roughly what `RichText` renders on the public site. Not identical —
         * the public one applies §2's tokens — but close enough that a
         * heading looks like a heading while it is being written.
         */
        class: 'prose prose-sm max-w-none px-4 py-3 focus:outline-none min-h-48',
      },
      /*
       * §4.2 — "Pasting from Word strips inline styles, `<font>`, `class`, and
       * `style` attributes." Intercepted here rather than left to TipTap's
       * schema: TipTap would drop the *tags* it does not know and keep the
       * attributes on the ones it does, which is how `class="MsoNormal"`
       * survives into the saved HTML.
       */
      handlePaste: (_view, event) => {
        const pasted = event.clipboardData?.getData('text/html');

        if (!pasted || !looksLikeOfficePaste(pasted)) {
          // Ordinary paste — let TipTap handle it, including plain text.
          return false;
        }

        event.preventDefault();
        editorRef.current?.commands.insertContent(cleanPastedHtml(pasted));

        return true;
      },
    },
    onUpdate: ({ editor: instance }) => {
      /*
       * Only the person typing changes the value.
       *
       * `emitUpdate: false` on `setContent` is documented to suppress this
       * callback and does not, so the guard cannot be a flag set around the
       * call — by the time TipTap emits, the flag is back to false. Focus is
       * the reliable discriminator: a programmatic push happens while the
       * caret is anywhere but here, and every toolbar command chains `.focus()`
       * before running.
       */
      if (applying.current || !instance.isFocused) {
        return;
      }

      const next = instance.getHTML();

      /*
       * TipTap serialises an empty document as `<p></p>`. Saved as-is, every
       * untranslated body would count as *translated* — CLAUDE.md rule 5's
       * whole point — and the completeness bar would read 100% for a page with
       * nothing on it.
       */
      onChange(setLocale(value, locale, instance.isEmpty ? '' : next));
    },
  });

  editorRef.current = editor;

  /*
   * Push the value in when the *locale* changes, or when the record loads.
   * Guarded on the editor's own HTML so typing does not fight the effect: the
   * `onUpdate` above has already told the form, and setting the same content
   * back would move the cursor to the start on every keystroke.
   */
  useEffect(() => {
    if (!editor) {
      return;
    }

    const current = editor.isEmpty ? '' : editor.getHTML();

    if (current !== html) {
      applying.current = true;
      editor.commands.setContent(html, { emitUpdate: false });
      applying.current = false;
    }
    // `html` is the only dependency that should re-run this; including the
    // editor instance is safe because it is stable for the component's life.
  }, [editor, html]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) {
    return null;
  }

  const filled = hasContent(value, locale);

  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required && locale === REQUIRED_LOCALE}
    >
      {(field) => (
        <div>
          <LocaleTabs value={value} />

          <div
            className={cn(
              'overflow-hidden rounded-md border border-subtle bg-surface',
              'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-navy-600',
              error && 'border-danger',
            )}
          >
            <RichTextToolbar
              editor={editor}
              showSource={showSource}
              onToggleSource={() => setShowSource((current) => !current)}
              // §4.2 — Super Admin only.
              canViewSource={hasRole('super-admin')}
              disabled={disabled}
            />

            {showSource ? (
              <textarea
                {...field}
                value={html}
                disabled={disabled}
                onChange={(event) => onChange(setLocale(value, locale, event.target.value))}
                className="min-h-48 w-full resize-y bg-surface px-4 py-3 font-mono text-body-sm text-primary outline-none"
              />
            ) : (
              <EditorContent editor={editor} {...field} />
            )}
          </div>

          {locale !== REQUIRED_LOCALE && !filled ? (
            <p className="mt-1.5 flex flex-wrap items-center gap-2 text-caption text-muted">
              {t('form.translationMissing', { locale: locale.toUpperCase() })}

              {hasContent(value, REQUIRED_LOCALE) ? (
                <button
                  type="button"
                  onClick={() => onChange(copyLocale(value, REQUIRED_LOCALE, locale))}
                  className="font-semibold text-link underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
                >
                  {t('form.copyFromGeorgian')}
                </button>
              ) : null}
            </p>
          ) : null}
        </div>
      )}
    </Field>
  );
}

/**
 * The same KA|EN control `TranslatableField` renders, bound to the same shell
 * state — so a screen with a title field and a body field switches both at
 * once, which is §4.1's point.
 */
function LocaleTabs({ value }: { value: TranslatedValue | null | undefined }) {
  const { t } = useTranslation();
  const { locale, setLocale: setEditorLocale } = useEditorLocale();

  return (
    <div
      role="radiogroup"
      aria-label={t('editor.richText.locale')}
      className="mb-1 flex items-center justify-end gap-1"
    >
      {(['ka', 'en'] as const).map((candidate) => (
        <button
          key={candidate}
          type="button"
          role="radio"
          aria-checked={locale === candidate}
          onClick={() => setEditorLocale(candidate)}
          className={cn(
            'rounded-full px-2.5 py-1 text-caption font-extrabold uppercase transition-colors duration-fast',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600',
            locale === candidate
              ? 'bg-navy-600 text-white'
              : 'bg-surface-alt text-secondary hover:text-primary',
          )}
        >
          {candidate}
          <span
            aria-hidden="true"
            className={cn(
              'ms-1 inline-block h-1.5 w-1.5 rounded-full align-middle',
              hasContent(value, candidate) ? 'bg-success' : 'bg-current opacity-40',
            )}
          />
          <span className="sr-only">
            {hasContent(value, candidate) ? t('form.localeFilled') : t('form.localeEmpty')}
          </span>
        </button>
      ))}
    </div>
  );
}
